import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faBarcodeRead } from '@fortawesome/pro-regular-svg-icons/faBarcodeRead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash.debounce';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { convertInputBufferToOriginalString } from 'ui-components/services/hotkey-service/convert-input-buffer-to-string';

import { messages } from './messages';
import styles from './styles.module.less';
import ProductCodeEnterHotkey from '../../../../../../../components/hotkeys/product-code-enter';
import { UserSettingsOptions } from '../../../../../../../enums/user-settings/user-settings-options';
import { useVerifyItemsBySetting } from '../../../../../../../hooks/use-user-setting';
import { useScanVerificationContext } from '../scan-verification-context-provider';

import type { FormEvent } from 'react';
import type React from 'react';

// at this point I am aware of two different scan inputs:
// * in original scan to verify functionality -> ss-ui-client\src\scan\verify\verify-header\product-code-input\index.js
// * in scan to receive/purchase orders -> ss-ui-client\src\components\scan-to-receive\scan-product\scan-product.js
// you can think about common component extraction later - at least the look and scan logic: sounds, debounce etc
// TODO: https://auctane.atlassian.net/browse/SPD-24897
export const ScanInput = () => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const scanByFields: string[] = useVerifyItemsBySetting();

  const { onScan, isSubmitting } = useScanVerificationContext();

  const intl = useIntl();

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const submitForm = useCallback(
    (event: FormEvent) => {
      if (event) {
        event.preventDefault();
      }
      onScan(value);
      setValue('');
      inputRef?.current?.focus();
    },
    [onScan, value],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        submitForm(event);
      }
    },
    [submitForm],
  );

  const placeholder = useMemo(() => {
    if (scanByFields.includes(UserSettingsOptions.upc.settingValue)) {
      if (
        scanByFields.includes(UserSettingsOptions.itemSku.settingValue) ||
        scanByFields.includes(UserSettingsOptions.fulfillmentSku.settingValue)
      ) {
        return intl.formatMessage(messages.verifyHeaderBoth);
      }
      return intl.formatMessage(messages.verifyHeaderUpc);
    }
    return intl.formatMessage(messages.verifyHeaderSku);
  }, [intl, scanByFields]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value), []);

  const handleScan = useCallback(
    (input: string[]) => {
      return debounce(() => {
        onScan(convertInputBufferToOriginalString(input));
        input.splice(0, input.length);
        setValue('');
        inputRef?.current?.focus();
      }, 300);
    },
    [onScan],
  );

  return (
    <ProductCodeEnterHotkey onProductCodeEnter={handleScan}>
      <form className={styles['scan-input-wrapper']} onSubmit={submitForm}>
        <input
          ref={inputRef}
          className={styles['scan-input']}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          type='text'
          value={value}
        />
        <Button
          ariaLabel={intl.formatMessage(messages.submitScan)}
          className={styles['autocomplete-scanner-icon']}
          disabled={!value || isSubmitting}
          onClick={submitForm}
          theme={THEME_CONSTANTS.unstyled}
        >
          <FontAwesomeIcon icon={faBarcodeRead} />
        </Button>
      </form>
    </ProductCodeEnterHotkey>
  );
};
